import type {
  ComputedFieldDefinition,
  ComputedFieldDefinitionId,
  ComputedFieldDefinitionWritablePayload,
} from "@repo/app-types/entities/computedField";
import {
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const computedFieldApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allComputedFieldDefinitions: builder.query<ComputedFieldDefinition[], void>(
      {
        query: () => `/computed_fields`,
        providesTags: (result) =>
          getTagsProvidedByList(TagType.ComputedFieldDefinition, result),
      },
    ),

    createComputedFieldDefinition: builder.mutation<
      ComputedFieldDefinition,
      ComputedFieldDefinitionWritablePayload
    >({
      query: (body) => ({
        method: "POST",
        url: `/computed_fields`,
        body,
      }),
      invalidatesTags: () =>
        getTagsInvalidatedByCreate(TagType.ComputedFieldDefinition),
    }),

    updateComputedFieldDefinition: builder.mutation<
      ComputedFieldDefinition,
      {
        id: ComputedFieldDefinitionId;
        body: ComputedFieldDefinitionWritablePayload;
      }
    >({
      query: ({ id, body }) => ({
        method: "PATCH",
        url: `/computed_fields/${encodeURIComponent(id)}`,
        body,
      }),
      invalidatesTags: (result, _error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.ComputedFieldDefinition, id),
    }),

    deleteComputedFieldDefinition: builder.mutation<
      void,
      ComputedFieldDefinitionId
    >({
      query: (id) => ({
        method: "DELETE",
        url: `/computed_fields/${encodeURIComponent(id)}`,
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          computedFieldApi.util.updateQueryData(
            "allComputedFieldDefinitions",
            undefined,
            (draft) => {
              const index = draft.findIndex((field) => field.id === id);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, id) =>
        getTagsInvalidatedByDelete(TagType.ComputedFieldDefinition, id),
    }),
  }),
});

export const {
  useAllComputedFieldDefinitionsQuery,
  useCreateComputedFieldDefinitionMutation,
  useUpdateComputedFieldDefinitionMutation,
  useDeleteComputedFieldDefinitionMutation,
} = computedFieldApi;
